import partition from "lodash/partition";
import * as React from "react";

import { MessagePathNode } from "@/shared/domain/topics";

import { TopicTreeNode } from "./buildTopicTree";

export function useExpandable(showChildren: boolean) {
  const [expanded, setExpanded] = React.useState<boolean>(false);

  React.useEffect(
    function syncExpandedStateWithChildren() {
      setExpanded(showChildren);
    },
    [showChildren],
  );

  return { expanded, setExpanded };
}

interface VisibleChildrenState {
  /** Whether to show children that are marked as hidden */
  showHidden: boolean;

  /** Callback to toggle whether to show children that are marked as hidden */
  setShowHidden: (showHidden: boolean) => void;

  /** The number of children that are marked as hidden */
  numHidden: number;

  /** The children, filtered to only show those that should be rendered. */
  children: TopicTreeNode<MessagePathNode>[];

  /** Whether we should show the option to show/hide filtered children. */
  showHiddenChildrenToggle: boolean;
}

export function useVisibleChildren(
  children: TopicTreeNode<MessagePathNode>[],
): VisibleChildrenState {
  const [showHidden, setShowHidden] = React.useState<boolean>(false);

  const numHidden = React.useMemo(() => {
    return children.filter((child) => child.hidden).length;
  }, [children]);

  const childrenToRender = React.useMemo(() => {
    const [visibleChildren, hiddenChildren] = partition(
      children,
      (child) => child.hidden === false,
    );
    return showHidden
      ? [...visibleChildren, ...hiddenChildren]
      : visibleChildren;
  }, [showHidden, children]);

  const showHiddenChildrenToggle = numHidden > 0 && numHidden < children.length;

  return {
    showHidden,
    setShowHidden,
    numHidden,
    children: childrenToRender,
    showHiddenChildrenToggle,
  };
}
